import { MobxLitElement } from '@adobe/lit-mobx';
import '@vaadin/date-picker';
import type { DatePicker, DatePickerValidatedEvent } from '@vaadin/date-picker';
import '@vaadin/date-picker/theme/material/vaadin-date-picker.js';
import { format } from 'date-fns/format.js';
import { isValid } from 'date-fns/isValid.js';
import { parse } from 'date-fns/parse.js';
import { parseISO } from 'date-fns/parseISO.js';
import { html, LitElement } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { IxDateNextStyles } from './ix-date-next-styles.js';
import {
  dateFormatState,
  defaultDateFormat,
  defaultTimeFormat,
} from './state/date-format-state.js';

export const NativeInputDateFormat = 'yyyy-MM-dd';

export class IxDateNext extends MobxLitElement {
  static get styles() {
    return [IxDateNextStyles];
  }

  static override shadowRootOptions = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true,
  };

  static readonly formAssociated = true;

  @property({ type: String }) type: 'default' | 'static' = 'default';

  @property({ type: String }) variant: 'date' | 'time' | 'date-time' = 'date';

  @property({ type: String }) format = defaultDateFormat;

  @property({ type: String }) timeFormat = defaultTimeFormat;

  @property({ type: String }) timezone = 'UTC';

  @property({ type: String }) label = '';

  @property({ type: String }) dateTimeText = '';

  @property({ type: String }) value;

  @property({ type: Boolean }) showCalendarOnMouseDown = false;

  @property({ type: String }) min = '';

  @property({ type: String }) max = '9999-12-31';

  /**
   * Applies when type=static. Defaults to `false`.
   *
   * If `true`, the value is formatted but no time zone conversions are applied.
   *
   * If `false`, values that do not specify a time zone are interpreted as being in the
   * user's local time zone and all values are displayed in the user's local time zone.
   */
  @property({ type: Boolean, attribute: 'no-time-zone-conversion' })
  noTimeZoneConversion = false;

  @property({ type: Boolean, reflect: true }) required = false;

  @property({ type: Boolean, reflect: true }) disabled = false;

  @property({ type: Boolean, reflect: true }) hideError = false;

  @property({ type: String, attribute: 'error-text' }) errorText = '';

  // eslint-disable-next-line class-methods-use-this
  @property({ type: Function }) onChanged: any = () => {};

  @query('vaadin-date-picker') datePicker!: any;

  @state() private dirty = false;

  private readonly internals = (this as HTMLElement) /* needed for closure */
    .attachInternals();

  private _lastClickWasToggle: boolean = false;

  private _firstUpdateComplete: boolean = false;

  get error() {
    return !!this.errorText;
  }

  get form() {
    return this.internals.form;
  }

  get labels() {
    return this.internals.labels;
  }

  get name() {
    return this.getAttribute('name') ?? '';
  }

  set name(name: string) {
    this.setAttribute('name', name);
  }

  get validationMessage() {
    return this.internals.validationMessage;
  }

  get validity() {
    return this.internals.validity;
  }

  get willValidate() {
    return this.internals.willValidate;
  }

  get nativeInputValue() {
    if (!this.value) return null;
    try {
      return new Date(this.value).toISOString().split('T')[0];
    } catch {
      return this.value;
    }
  }

  private _i18nApplied = false;

  async connectedCallback(): Promise<void> {
    super.connectedCallback();

    await dateFormatState.hydrateStore();

    IxDateNext.ensureOverlayStyle();
    document.addEventListener(
      'dateFormatChanged',
      this.handleDateFormatChanged,
    );

    if (!this.hasAttribute('format')) {
      this.format = dateFormatState.preferredDateFormat;
    }
    if (!this.hasAttribute('timeFormat')) {
      this.timeFormat = dateFormatState.preferredTimeFormat;
    }
    if (!this.hasAttribute('timezone')) {
      this.timezone = dateFormatState.preferredTimezone;
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener(
      'dateFormatChanged',
      this.handleDateFormatChanged,
    );
  }

  private _refreshPickerDisplay() {
    if (!this.datePicker) return;
    const v = this.datePicker.value ?? '';
    // force observer + reformat
    this.datePicker.value = '';
    this.datePicker.value = v;
  }

  /**
   * Returns "today at midnight" in the user's preferred timezone.
   */
  private _getTodayInTimezone(): Date {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: this.timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());

    const get = (type: string) => parts.find(p => p.type === type)?.value;
    const year = parseInt(get('year') ?? '0', 10);
    const month = parseInt(get('month') ?? '1', 10) - 1; // 0-indexed
    const day = parseInt(get('day') ?? '1', 10);

    return new Date(year, month, day, 0, 0, 0, 0);
  }

  async firstUpdated() {
    const input = this.datePicker?.querySelector('input');
    if (input) {
      input.addEventListener('mousedown', (e: MouseEvent) => {
        if (!this.showCalendarOnMouseDown) {
          e.preventDefault();
          e.stopImmediatePropagation();
          input.focus();
        }
      });
    }

    const minDate = new Date(this.min);
    const maxDate = new Date(this.max);

    if (minDate && !Number.isNaN(minDate.valueOf())) {
      const [min] = minDate.toISOString().split('T');
      this.min = min;
    }

    if (maxDate && !Number.isNaN(maxDate.valueOf())) {
      const [max] = maxDate.toISOString().split('T');
      this.max = max;
    }

    if (this.datePicker) {
      const originalOpen = this.datePicker.open.bind(this.datePicker);

      this.datePicker.open = (...args: any[]) => {
        if (!this.showCalendarOnMouseDown) {
          const isFromToggle = this._lastClickWasToggle;
          this._lastClickWasToggle = false;

          if (!isFromToggle) {
            return undefined;
          }
        }
        originalOpen(...args);
        return undefined;
      };

      const toggleButton = this.datePicker.shadowRoot?.querySelector(
        '[part="toggle-button"]',
      );
      if (toggleButton) {
        toggleButton.addEventListener('mousedown', () => {
          this._lastClickWasToggle = true;
        });
      }

      const originalCheckValidity = this.datePicker.checkValidity.bind(
        this.datePicker,
      );

      this.datePicker.checkValidity = () => {
        if (!this.dirty) {
          this.datePicker.invalid = false;
          return true;
        }
        return originalCheckValidity();
      };

      this.datePicker.addEventListener('invalid-changed', () => {
        if (!this.dirty && this.datePicker.invalid) {
          this.datePicker.invalid = false;
        }
      });

      // Override Vaadin's internal "today" methods to use timezone-aware date.
      // We patch the prototype of vaadin-month-calendar so all instances
      // (including dynamically created ones during scrolling) use our logic.
      const getTodayFn = () => this._getTodayInTimezone();
      const isTodayFn = (
        date: Date | { year: number; month: number; day: number } | null,
      ) => {
        if (!date) return false;
        const today = this._getTodayInTimezone();
        if (date instanceof Date) {
          return (
            date.getFullYear() === today.getFullYear() &&
            date.getMonth() === today.getMonth() &&
            date.getDate() === today.getDate()
          );
        }
        return (
          date.year === today.getFullYear() &&
          date.month === today.getMonth() &&
          date.day === today.getDate()
        );
      };

      // Override on date picker instance
      this.datePicker._getTodayMidnight = getTodayFn;
      this.datePicker._isToday = isTodayFn;

      // Patch the vaadin-month-calendar prototype so all instances use our _isToday
      const MonthCalendarClass = customElements.get('vaadin-month-calendar') as
        | ({ prototype: Record<string, unknown> } & { new (): HTMLElement })
        | undefined;
      if (MonthCalendarClass?.prototype) {
        MonthCalendarClass.prototype._isToday = isTodayFn;
      }

      // Override on overlay content when it becomes available
      const applyOverlayOverrides = () => {
        const overlay = this.datePicker._overlayContent;
        if (overlay) {
          overlay._getTodayMidnight = getTodayFn;
          overlay._isToday = isTodayFn;
          // Set initialPosition to our "today" so calendar opens to correct month
          overlay.initialPosition = getTodayFn();

          // Override _isTodayAllowed to use timezone-aware "today" for disabling the Today button
          overlay._isTodayAllowed = (min: Date | null, max: Date | null) => {
            const todayMidnight = getTodayFn();
            return (
              (!min || todayMidnight >= min) && (!max || todayMidnight <= max)
            );
          };

          // Force refresh of the Today button disabled state
          if (overlay._todayButton) {
            overlay._todayButton.disabled = !overlay._isTodayAllowed(
              overlay.minDate,
              overlay.maxDate,
            );
          }
        }
      };

      // Apply when overlay opens (content is lazily created)
      this.datePicker.addEventListener('opened-changed', (e: CustomEvent) => {
        if (e.detail.value) {
          // Small delay to ensure overlay content is ready
          requestAnimationFrame(() => applyOverlayOverrides());
        }
      });
    }

    if (this.datePicker) {
      const { i18n } = this.datePicker;
      i18n.formatDate = ({ year, month, day }: any): string => {
        if (
          ![year, month, day].every(Number.isFinite) ||
          month < 0 ||
          month > 11
        )
          return '';
        const d = new Date(year, month, day);
        return Number.isNaN(d.valueOf()) ? '' : format(d, this.format);
      };
      i18n.parseDate = (s: string) => {
        const str = (s ?? '').trim();
        if (!str) return undefined;
        const d = parse(str, this.format, new Date(0));
        if (!isValid(d)) return undefined;
        try {
          if (format(d, this.format) !== str) return undefined;
        } catch {
          return undefined;
        }
        return { year: d.getFullYear(), month: d.getMonth(), day: d.getDate() };
      };
      this.datePicker.i18n = { ...i18n };
      this._i18nApplied = true;

      await Promise.resolve();
      await new Promise<void>(resolve => {
        requestAnimationFrame(() => resolve());
      });
      this._refreshPickerDisplay();
    }

    this.updateValidity();
    this._firstUpdateComplete = true;
  }

  protected override updated(changed: Map<string | number | symbol, unknown>) {
    if (changed.has('format') && this._i18nApplied && this.datePicker) {
      this._refreshPickerDisplay();
    }

    if (!this._firstUpdateComplete) return;

    if (changed.has('disabled') && this.disabled) {
      this.errorText = '';
      this.internals.setValidity({}, '');
      if (this.datePicker) {
        this.datePicker.invalid = false;
        this.datePicker.removeAttribute('invalid');
      }
    } else if (changed.has('value')) {
      if (this.datePicker) {
        this.internals.setValidity(
          this.dirty && this.error ? { badInput: true } : {},
          this.dirty && this.error ? this.errorText : '',
          this.datePicker,
        );
      }
    } else if (
      changed.has('min') ||
      changed.has('max') ||
      changed.has('required') ||
      changed.get('disabled') === false
    ) {
      this.validateDate();
    }

    this.internals.setFormValue(this.value);
  }

  /** @private */
  formResetCallback() {
    this.reset();
  }

  /**
   * Reset the text field to its default value.
   */
  reset() {
    this.clear();
    this.value = this.getAttribute('value');
  }

  validate() {
    return this.reportValidity();
  }

  reportValidity() {
    if (!this.datePicker || !this._firstUpdateComplete) return true;
    this.dirty = true;
    this.validateDate();

    const originalMessage = this.internals.validationMessage;
    const isThisValid = this.internals.checkValidity();

    if (!isThisValid && !this.disabled) {
      this.internals.setValidity({ customError: true }, originalMessage);
      this.datePicker.setAttribute('invalid', '');
      this.datePicker.invalid = true;
    } else {
      this.internals.setValidity({}, '');
      this.datePicker.removeAttribute('invalid');
      this.datePicker.invalid = false;
    }

    return isThisValid;
  }

  validateDate = () => {
    if (this.disabled) return;

    if (!(this.datePicker instanceof HTMLElement)) {
      return;
    }

    let errorMsg = '';
    if (!this.dirty) {
      this.internals.setValidity({}, '', this.datePicker);
      return;
    }

    if (!this.value) {
      if (!this.required) {
        errorMsg = '';
      } else {
        errorMsg = `${this.label || 'This field'} is required.`;
      }
    } else {
      const date = new Date(this.value);
      if (!date || Number.isNaN(date.valueOf())) {
        errorMsg = 'Invalid date format.';
      } else {
        if (this.min) {
          const minDate = new Date(this.min);
          if (date < minDate) {
            errorMsg = `${this.label || 'Date'} cannot be earlier than ${this.formatValueAsDate(minDate)}.`;
          }
        }

        if (!errorMsg && this.max) {
          const maxDate = new Date(this.max);
          if (date > maxDate) {
            errorMsg = `${this.label || 'Date'} cannot be later than ${this.formatValueAsDate(maxDate)}.`;
          }
        }
      }
    }

    if (!this.hideError) this.errorText = errorMsg;

    this.internals.setValidity(
      errorMsg ? { valueMissing: true } : {},
      errorMsg,
      this.datePicker,
    );
  };

  private updateValidity() {
    if (this.required && !this.value) {
      this.internals.setValidity(
        { valueMissing: true },
        'This field is required.',
      );
    } else {
      this.internals.setValidity({});
    }
  }

  handleDateFormatChanged = async () => {
    await dateFormatState.hydrateStore();

    this.format = dateFormatState.preferredDateFormat;
    this.timeFormat = dateFormatState.preferredTimeFormat;
    this.timezone = dateFormatState.preferredTimezone;

    await this.updateComplete;
    // if datepicker is available re-set the value to trigger formatting with new format
    if (this.datePicker) {
      const v = this.datePicker.value;
      this.datePicker.value = '';
      this.datePicker.value = v;
    }
  };

  handleMouseDown = (e: Event) => {
    if (this.showCalendarOnMouseDown) {
      const inputElement = e.target as HTMLInputElement;

      if ('showPicker' in inputElement) {
        inputElement.showPicker();
      }
    }
  };

  handleValidated = (e: DatePickerValidatedEvent) => {
    const field = e.target as DatePicker;
    const value =
      field.value || (field.inputElement as HTMLInputElement).value || null;

    this.value = value;
    this.dirty = true;
    this.validateDate();
    this.onChanged(value);
  };

  private markDirty = () => {
    this.dirty = true;
  };

  parseValue = () => {
    if (typeof this.value !== 'string') {
      return this.value;
    }

    if (this.noTimeZoneConversion) {
      // Matches the time zone designator, e.g. `Z` or `-02:00`.
      // Note: does not match `-02` and `-0200`, which are valid ISO 8601 but rarely used.
      const timeZoneRegex = /Z|([+-]\d\d:\d\d)/;
      const valueWithoutTimeZone = this.value.split(timeZoneRegex)[0];
      return parseISO(valueWithoutTimeZone);
    }

    return parseISO(this.value);
  };

  getTimezoneDateTime = (date: Date) => {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: this.timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(new Date(date));

    const get = (type: string) => parts.find(p => p.type === type)?.value;

    const year = get('year');
    const month = get('month');
    const day = get('day');
    const hour = get('hour');
    const minute = get('minute');
    const second = get('second');

    return new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
  };

  formatValueAsDate = (date: Date) =>
    format(date, this.format || dateFormatState.preferredDateFormat);

  formatValueAsTime = (date: Date) =>
    format(date, this.timeFormat || dateFormatState.preferredTimeFormat);

  formatValueAsDateTime = (date: Date) =>
    `${this.formatValueAsDate(date)}${
      this.dateTimeText ? ` ${this.dateTimeText} ` : ' '
    }${this.formatValueAsTime(date)}`;

  formatValue = () => {
    const parsedDate = this.parseValue();

    if (!isValid(parsedDate)) return '';

    const adjustedDate = this.noTimeZoneConversion
      ? parsedDate
      : this.getTimezoneDateTime(parsedDate);

    switch (this.variant) {
      case 'date':
        return this.formatValueAsDate(adjustedDate);
      case 'time':
        return this.formatValueAsTime(adjustedDate);
      case 'date-time':
        return this.formatValueAsDateTime(adjustedDate);
      default:
        return '';
    }
  };

  clear = () => {
    this.value = null;
    this.dirty = false;
    this.errorText = '';

    if (this.datePicker) {
      this.datePicker.value = undefined;
    }
  };

  // Inject some styles globally because we can't access from scoped :host css
  private static ensureOverlayStyle() {
    if (!document.querySelector('#ix-date-overlay-style')) {
      const style = document.createElement('style');
      style.id = 'ix-date-overlay-style';
      style.textContent = `
      vaadin-date-picker-overlay {
        --material-secondary-text-color: var(--clr-primary, #1456e0);
        --material-primary-color: initial;
        --material-primary-contrast-color: initial;
        z-index: 99999;
      }

      vaadin-date-picker-overlay-content vaadin-date-picker-year-scroller {
         display: none;
      }

      vaadin-date-picker-overlay-content [slot="months"] {
        margin-right: 0;
      }

      vaadin-date-picker-overlay-content::part(toolbar) {
        margin-right: 0 !important;
      }

      vaadin-date-picker-overlay-content vaadin-month-calendar::part(month-header) {
        padding-top: 0;
      }

      vaadin-date-picker-overlay-content vaadin-month-calendar::part(today) {
        color: var(--clr-primary, #1456e0);
      }

      vaadin-date-picker-overlay-content vaadin-month-calendar::part(disabled) {
        color: var(--material-disabled-text-color);
      }

      vaadin-date-picker-overlay-content vaadin-month-calendar::part(selected) {
        color: #ffffff;
      }

      vaadin-date-picker-overlay-content vaadin-month-calendar::part(date):hover::after {
        background-color: #00000000;
        border-color: #00000000;
      }

      vaadin-date-picker-overlay-content vaadin-month-calendar::part(focused)::before {
        animation: none;
        box-shadow: none;
        background-color: var(--lumo-primary-color-10pct);
      }

      vaadin-date-picker-overlay-content vaadin-month-calendar::part(selected)::before {
        background-color: var(--_selection-color);
      }

      vaadin-date-picker-overlay-content vaadin-month-calendar::part(selected)::before {
        animation: none;
        box-shadow: none;
      }

      vaadin-date-picker-overlay-content vaadin-month-calendar::part(focused)::after {
        border: none;
      }

      vaadin-date-picker-overlay-content vaadin-month-calendar::part(selected)::after{
        border-color: #00000000;
      }
    `;
      document.head.appendChild(style);
    }
  }

  renderStaticDate() {
    return html`<span>${this.formatValue()}</span>`;
  }

  renderDateInput() {
    return html`<vaadin-date-picker
      label=${this.label}
      .value=${this.nativeInputValue || ''}
      .errorMessage=${!this.hideError ? this.errorText : null}
      @input=${this.markDirty}
      @validated=${this.handleValidated}
      ?disabled=${this.disabled}
      ?required=${this.required}
      min=${this.min}
      max=${this.max}
    ></vaadin-date-picker>`;
  }

  render() {
    return this.type === 'static'
      ? this.renderStaticDate()
      : this.renderDateInput();
  }
}
